import { MenuDivider, MenuGroup, MenuItem, MenuList, useColorModeValue, useDisclosure } from '@chakra-ui/react';
import { DrawerType } from '@pelicargo/types';
import { HiOutlineClipboard } from 'react-icons/hi2';

import { useQuickSendUpdate } from '../../hooks/emails/useQuickSendUpdate';
import { GetRequestOutput } from '../../hooks/request/useRequest';
import { useRequestEmails } from '../../hooks/useRequestEmails';
import { useRequestDrawersContext } from '../RequestDrawers';
import { EmailListMenuItem } from './EmailListMenuItem';
import { SubMenu } from './SubMenu';

export const RequestEmailsMenuList = ({ request }: { request: GetRequestOutput }) => {
  const { isSending, ref: quickSendUpdateRef } = useQuickSendUpdate<HTMLButtonElement>();
  const customerMenuDisclosure = useDisclosure();
  const airlineMenuDisclosure = useDisclosure();
  const menuItemHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const { openModal } = useRequestDrawersContext();
  const {
    handleClickSendBooking,
    handleQuickSelectDrawer,
    copyLink,
    bookingConfirmationSlug,
    marketplaceBookingSlug,
    quoteListingSlug,
  } = useRequestEmails({ request });

  return (
    <MenuList>
      <MenuGroup title="Emails">
        <EmailListMenuItem drawer="ProviderQuoting" onClick={() => openModal('ProviderQuoting')} command="Provider">
          Request Quote
        </EmailListMenuItem>
        <EmailListMenuItem
          drawer="QuoteUpdate"
          onClick={() => openModal('QuoteUpdate')}
          command="Customer"
          name="sendUpdate"
          isDisabled={isSending}
          ref={quickSendUpdateRef}
        >
          Send Update
        </EmailListMenuItem>
        <EmailListMenuItem drawer="AirlineBooking" onClick={() => openModal('AirlineBooking')} command="Airline">
          Book Quote
        </EmailListMenuItem>
        <EmailListMenuItem drawer="BookingConfirmation" onClick={handleClickSendBooking} command="Customer">
          Send Booking
        </EmailListMenuItem>
        <MenuDivider />
        <SubMenu
          label="Customer"
          items={[
            { drawer: 'NewRate', label: 'Send New Rate', onClick: () => openModal('NewRate') },
            {
              drawer: 'RequestClarifications',
              label: 'Request Clarifications',
              onClick: () => openModal('RequestClarifications'),
            },
            {
              drawer: 'StatusUpdate',
              label: 'Shipment Update',
              isDisabled: !request?.booked_quote_variant_id,
              onClick: () =>
                openModal('StatusUpdate', {
                  name: 'Shipment Status Update',
                  subject: 'Shipment Update',
                }),
            },
          ]}
          isOpen={customerMenuDisclosure.isOpen}
          onOpen={customerMenuDisclosure.onOpen}
          onClose={customerMenuDisclosure.onClose}
          otherMenuDisclosure={airlineMenuDisclosure}
          menuItemHoverBg={menuItemHoverBg}
        />
        <SubMenu
          label="Airline"
          items={[
            {
              drawer: 'RequestSchedule',
              label: 'Request Schedule',
              onClick: () => handleQuickSelectDrawer(DrawerType.RequestSchedule),
            },
            { drawer: 'BookingChange', label: 'Change Booking', onClick: () => openModal('BookingChange') },
            { drawer: 'BookingCancel', label: 'Cancel Booking', onClick: () => openModal('BookingCancel') },
          ]}
          isOpen={airlineMenuDisclosure.isOpen}
          onOpen={airlineMenuDisclosure.onOpen}
          onClose={airlineMenuDisclosure.onClose}
          otherMenuDisclosure={customerMenuDisclosure}
          menuItemHoverBg={menuItemHoverBg}
        />
      </MenuGroup>
      <MenuDivider />
      <MenuGroup title="Links">
        <MenuItem
          onClick={copyLink(quoteListingSlug)}
          icon={<HiOutlineClipboard />}
          command="l > q"
          _hover={{ bg: menuItemHoverBg }}
          sx={{ '& > .chakra-menu__command': { fontFamily: 'monospace' } }}
        >
          Quote List
        </MenuItem>
        <MenuItem
          onClick={copyLink(bookingConfirmationSlug)}
          isDisabled={!bookingConfirmationSlug}
          icon={<HiOutlineClipboard />}
          command="l > b"
          _hover={{ bg: menuItemHoverBg }}
          sx={{ '& > .chakra-menu__command': { fontFamily: 'monospace' } }}
        >
          Booking Confirmation
        </MenuItem>
        <MenuItem
          onClick={copyLink(marketplaceBookingSlug)}
          isDisabled={!marketplaceBookingSlug}
          icon={<HiOutlineClipboard />}
          command="l > m"
          _hover={{ bg: menuItemHoverBg }}
          sx={{ '& > .chakra-menu__command': { fontFamily: 'monospace' } }}
        >
          Marketplace
        </MenuItem>
      </MenuGroup>
    </MenuList>
  );
};
